/**
 * Active-org localStorage primitive (super-admin cross-lab access — Phase 24).
 *
 * Deliberately a STANDALONE leaf module with ZERO React / axios / app imports. It holds only the
 * storage key and the pure read/write helpers, so `src/api/client.ts` (a low-level foundational
 * module) can import `getActiveOrgId()` to inject the `X-Active-Org` header WITHOUT pulling in
 * `OrgContext.tsx`'s React import graph. This breaks what would otherwise be a
 * `client.ts → OrgContext.tsx → adminOrgs.ts → client.ts` module-init cycle.
 *
 * `OrgContext.tsx` re-exports `getActiveOrgId` from here so existing call sites can import it
 * from either module.
 */
export const ORG_STORAGE_KEY = "eddkit.active_org";

export function readStoredId(): string | null {
  try {
    return localStorage.getItem(ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeStoredId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ORG_STORAGE_KEY, id);
    else localStorage.removeItem(ORG_STORAGE_KEY);
  } catch {
    /* localStorage unavailable (private mode) — the active lab just won't persist */
  }
}

/**
 * Standalone read of the persisted active org id (no React). Imported by `src/api/client.ts`
 * to inject the `X-Active-Org` header. Returns null when unset.
 */
export function getActiveOrgId(): string | null {
  return readStoredId();
}
